import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import './count-card.scss';
import AdvancePopover from '../advance-popover/advance-popover';
import BesicTable from '../table/basicTable';
import '../table/table.scss';
import { ArrowCircleDownIcon } from '../icons/icons';
import Skeleton from '../skeleton/skeleton';

/**
 * @typedef {'left' | 'center' | 'right'} AlignType - Size choices for the Demo component.
 */

/**
 * Component to demonstrate different sizes, appearances, and popup options.
 * @param {Object} props
 * @param {AlignType} props.align
 * @returns {JSX.Element}
 */

export default function CountCard({
  data,
  label,
  onClick,
  enableDynamicTable,
  columns,
  disableBorder,
  align,
  disableClick,
  enableIcon,
  popoverPlacement,
  iconVisible,
  beforeIcon,
  className,
  tableClass,
  zIndex,
  isList,
  children,
  labelClass,
  isLoading,
}) {
  const alignProperties = {
    left: 'left',
    center: 'center',
    right: 'right',
  };
  const alignProps = alignProperties[align];
  const [t] = useTranslation('manageorders');
  const [eventValue, setEventValue] = useState();
  const [showIcon, setShowIcon] = useState(false);

  const togglePopover = (e) => {
    if (!disableClick) {
      setEventValue(e.target);
    }
    onClick();
  };
  const handleClose = () => {
    setEventValue(null);
  };

  return (
    <div className={`count-card-container${` ${alignProps}`}`}>
      <button
        className={`count-card${disableBorder ? ' disable-border' : ''}${
          className ? ` ${className}` : ''
        }`}
        type="button"
        onClick={togglePopover}
        onMouseEnter={() => {
          setShowIcon(true);
        }}
        onMouseLeave={() => {
          setShowIcon(false);
        }}
        id="countPopover"
      >
        {beforeIcon && beforeIcon}
        <span
          className={`${beforeIcon ? 'ml-5' : ''}${
            labelClass ? ` ${labelClass}` : ''
          }`}
        >
          {label || children}
        </span>
        {disableBorder && enableIcon && showIcon && (
          <ArrowCircleDownIcon className="arrow-icon" />
        )}
        {iconVisible && (
          <span className="arrow-icon">
            <ArrowCircleDownIcon />
          </span>
        )}
      </button>
      <AdvancePopover
        reference="countPopover"
        getEvent={eventValue}
        closePopover={handleClose}
        placement={popoverPlacement}
        zIndex={zIndex}
      >
        {!isList && (
          <div
            className={`count-card-table${tableClass ? ` ${tableClass}` : ''}`}
          >
            {isLoading && (
              <div className="custom-table-container">
                <table>
                  <thead>
                    <tr>
                      {[...Array(6)].map(() => (
                        <th>
                          <Skeleton skHeight="0.694vw" skWidth="6.944vw" />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(2)].map(() => (
                      <tr>
                        {[...Array(6)].map(() => (
                          <td>
                            <Skeleton skHeight="0.694vw" skWidth="6.944vw" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && !enableDynamicTable && (
              <div className="custom-table-container">
                <table>
                  <thead>
                    <tr>
                      <th align="left">
                        {t('manageordersscreen.totalitemspopup.productname')}
                      </th>
                      <th align="center">
                        {t('manageordersscreen.totalitemspopup.deliverydate')}
                      </th>
                      <th align="center">
                        {t('manageordersscreen.totalitemspopup.quantity')}
                      </th>
                      <th align="center">
                        {t('manageordersscreen.totalitemspopup.weight')}
                      </th>
                      <th align="center">
                        {t('manageordersscreen.totalitemspopup.priceperunit')}
                      </th>
                      <th align="center">
                        {t('manageordersscreen.totalitemspopup.totalprice')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.map((val) => (
                      <tr key={val?.id || val?.itemName}>
                        <td align="left">{val?.itemName}</td>
                        <td align="center">{val?.dueDate}</td>
                        <td align="center">{val?.quantity}</td>
                        <td align="center">
                          {val?.grossWeight} {val?.weightUnit}
                        </td>
                        <td align="center">{val?.price}</td>
                        <td align="center">{val?.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && enableDynamicTable && (
              <BesicTable
                data={data}
                columns={columns}
                className="auto-column-width"
              />
            )}
          </div>
        )}
        {isList &&
          data?.map((ele) => (
            <ul className="action-list">
              <li>
                <button type="button">{ele}</button>
              </li>
            </ul>
          ))}
      </AdvancePopover>
    </div>
  );
}
CountCard.propTypes = {
  data: PropTypes.arrayOf(Object),
  columns: PropTypes.arrayOf(Object),
  label: PropTypes.string,
  onClick: PropTypes.func,
  enableDynamicTable: PropTypes.bool,
  disableBorder: PropTypes.bool,
  align: PropTypes.string,
  disableClick: PropTypes.bool,
  enableIcon: PropTypes.bool,
  popoverPlacement: PropTypes.string,
  iconVisible: PropTypes.bool,
  beforeIcon: PropTypes.element,
  className: PropTypes.string,
  tableClass: PropTypes.string,
  zIndex: PropTypes.number,
  isList: PropTypes.bool,
  children: PropTypes.node,
  labelClass: PropTypes.string,
  isLoading: PropTypes.bool,
};
CountCard.defaultProps = {
  data: [],
  columns: [],
  label: '',
  onClick: () => {},
  enableDynamicTable: false,
  disableBorder: false,
  align: 'left',
  disableClick: false,
  enableIcon: false,
  popoverPlacement: 'center',
  iconVisible: false,
  beforeIcon: '',
  className: '',
  tableClass: '',
  zIndex: 999999,
  isList: false,
  children: null,
  labelClass: '',
  isLoading: false,
};
